import { Injectable, isDevMode } from '@angular/core';
import { Mod } from '../models/item-data-models';

interface ModPattern {
  mod: Mod;
  regex: RegExp;
  ranges: Array<{ min: number; max: number }>;
  patternKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class ModMatcherService {
  private patterns: ModPattern[] = [];
  private patternKeyIndex: Map<string, ModPattern[]> = new Map();
  private readonly debugMode: boolean;

  constructor() {
    // Enable debug mode in non-production builds
    this.debugMode = isDevMode();
  }

  /**
   * Builds pattern cache from mods. Should be called once after mods are loaded.
   */
  buildPatternCache(mods: Mod[]): void {
    this.patterns = [];
    this.patternKeyIndex.clear();

    for (const mod of mods) {
      const pattern = this.buildPattern(mod);
      if (pattern) {
        this.patterns.push(pattern);

        // Index by pattern key for faster lookup
        const key = pattern.patternKey;
        if (!this.patternKeyIndex.has(key)) {
          this.patternKeyIndex.set(key, []);
        }
        this.patternKeyIndex.get(key)!.push(pattern);
      }
    }
  }

  /**
   * Builds a pattern from a mod's text template.
   */
  private buildPattern(mod: Mod): ModPattern | null {
    const text = mod.text.trim();
    if (!text) {
      return null;
    }

    // Extract ranges and build regex pattern
    const ranges: Array<{ min: number; max: number }> = [];

    // Pattern to match bracketed ranges: (min-max), (value), +(min-max), etc.
    // Also handles negative numbers and decimals: (-5--3), (3.81-4.4), (8-8.9)
    const rangePattern = /([+\-]?)\s*\(([\d.\-]+)\s*-\s*([\d.\-]+)\)/g;
    const singleValuePattern = /([+\-]?)\s*\(([\d.\-]+)\)/g;

    // Build regex by processing text in segments
    // First, find all range positions and extract them
    interface RangeMatch {
      start: number;
      end: number;
      replacement: string;
    }

    const rangeMatches: RangeMatch[] = [];
    let match;

    // Find all (min-max) patterns
    rangePattern.lastIndex = 0;
    while ((match = rangePattern.exec(text)) !== null) {
      const min = parseFloat(match[2]);
      const max = parseFloat(match[3]);
      if (!isNaN(min) && !isNaN(max)) {
        ranges.push({ min, max });
        const prefix = match[1] || '';
        const escapedPrefix = prefix === '+' || prefix === '-' ? '\\' + prefix : prefix;
        // Check if there's leading whitespace in the match (from \s* before the opening parenthesis)
        const leadingWhitespace = match[0].match(/^\s+/);
        const whitespacePattern = leadingWhitespace ? '\\s+' : '';
        // Use pattern that matches decimals: \d+\.?\d* matches integers and decimals
        rangeMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          replacement: whitespacePattern + escapedPrefix + '(\\d+\\.?\\d*)'
        });
      }
    }

    // Find all (value) patterns
    singleValuePattern.lastIndex = 0;
    while ((match = singleValuePattern.exec(text)) !== null) {
      // Skip if already matched as (min-max)
      if (rangeMatches.some(rm => rm.start <= match!.index && rm.end >= match!.index + match![0].length)) {
        continue;
      }
      const value = parseFloat(match[2]);
      if (!isNaN(value)) {
        ranges.push({ min: value, max: value });
        const prefix = match[1] || '';
        const escapedPrefix = prefix === '+' || prefix === '-' ? '\\' + prefix : prefix;
        // Check if there's leading whitespace in the match (from \s* before the opening parenthesis)
        const leadingWhitespace = match[0].match(/^\s+/);
        const whitespacePattern = leadingWhitespace ? '\\s+' : '';
        // Use pattern that matches decimals: \d+\.?\d* matches integers and decimals
        rangeMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          replacement: whitespacePattern + escapedPrefix + '(\\d+\\.?\\d*)'
        });
      }
    }

    // Sort matches by position (forward order to build regex segment by segment)
    rangeMatches.sort((a, b) => a.start - b.start);

    // Build regex string by processing text segment by segment
    let regexStr = '';
    let lastIndex = 0;

    for (const rm of rangeMatches) {
      // Escape the text before this range
      const before = text.substring(lastIndex, rm.start);
      const escapedBefore = before.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      regexStr += escapedBefore;

      // Add the capture group (already a regex pattern, don't escape)
      regexStr += rm.replacement;

      lastIndex = rm.end;
    }

    // Escape the remaining text after the last range
    const after = text.substring(lastIndex);
    const escapedAfter = after.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    regexStr += escapedAfter;

    // If no ranges found, escape the entire string
    if (rangeMatches.length === 0) {
      regexStr = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // Normalize whitespace - allow flexible whitespace matching
    // This handles cases where the template has "Adds (23-35) to (39-59)" and actual text has "Adds 30 to 46"
    regexStr = regexStr.replace(/\s+/g, '\\s+');

    // Handle newlines - match them flexibly
    regexStr = regexStr.replace(/\\n/g, '\\s*\\n?\\s*');

    // Create pattern key from stable parts of the text (remove ranges)
    const patternKey = this.createPatternKey(text);

    try {
      // Case-insensitive matching
      const regex = new RegExp('^' + regexStr + '$', 'i');
      return {
        mod,
        regex,
        ranges,
        patternKey
      };
    } catch (error) {
      console.warn(`Failed to build regex pattern for mod "${mod.name}": ${text}`, error);
      return null;
    }
  }

  /**
   * Creates a pattern key from mod text by removing bracketed ranges.
   * This is used for fast lookup to narrow down candidate mods.
   */
  private createPatternKey(text: string): string {
    // Remove all bracketed ranges and normalize whitespace
    // Updated to handle decimals: (3.81-4.4), (8-8.9), etc.
    let key = text
      .replace(/[+\-]?\s*\([\d.\-\s]+\)/g, '') // Remove (min-max) and (value), including decimals
      .replace(/[+\-]?\d+\.?\d*/g, '') // Remove standalone numbers (including decimals and +/- prefixes)
      .replace(/%/g, '') // Remove percentage signs
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .toLowerCase();

    // Use first few significant words as key
    const words = key.split(/\s+/).filter(w => w.length > 2);
    if (words.length === 0) {
      return key;
    }

    // Use first 2-3 words as key
    return words.slice(0, Math.min(3, words.length)).join(' ');
  }

  /**
   * Matches explicit mod text against mod patterns and returns matching mod names.
   * Returns array of mod names that match (can be multiple if same values appear in different tiers).
   */
  matchModText(explicitModText: string): string[] {
    if (!explicitModText || !explicitModText.trim()) {
      return [];
    }

    const normalizedText = explicitModText.trim();
    const matches: string[] = [];

    if (this.debugMode) {
      console.log(`[ModMatcher] Matching mod text: "${normalizedText}"`);
    }

    // Try to narrow down candidates using pattern key
    const candidateKey = this.createPatternKey(normalizedText);
    const candidates = this.patternKeyIndex.get(candidateKey) || this.patterns;

    if (this.debugMode) {
      console.log(`[ModMatcher] Pattern key: "${candidateKey}", Candidates: ${candidates.length} (out of ${this.patterns.length} total)`);
    }

    // Try each candidate pattern
    for (const pattern of candidates) {
      const match = normalizedText.match(pattern.regex);
      if (match) {
        // Extract captured values and validate against ranges
        let valid = true;
        let captureIndex = 1; // First capture group is at index 1
        const capturedValues: number[] = [];

        for (const range of pattern.ranges) {
          if (captureIndex >= match.length) {
            valid = false;
            if (this.debugMode) {
              console.log(`[ModMatcher] Pattern "${pattern.mod.name}" matched regex but missing capture group ${captureIndex}`);
            }
            break;
          }

          const capturedValue = parseFloat(match[captureIndex]);
          if (isNaN(capturedValue)) {
            valid = false;
            if (this.debugMode) {
              console.log(`[ModMatcher] Pattern "${pattern.mod.name}" matched regex but capture group ${captureIndex} is not a number: "${match[captureIndex]}"`);
            }
            break;
          }

          capturedValues.push(capturedValue);

          // Validate value is within range
          if (capturedValue < range.min || capturedValue > range.max) {
            valid = false;
            if (this.debugMode) {
              console.log(`[ModMatcher] Pattern "${pattern.mod.name}" matched regex but value ${capturedValue} is outside range [${range.min}, ${range.max}]`);
            }
            break;
          }

          captureIndex++;
        }

        if (valid) {
          matches.push(pattern.mod.name);
          if (this.debugMode) {
            console.log(`[ModMatcher] ✓ Matched "${pattern.mod.name}" with values: [${capturedValues.join(', ')}]`);
          }
        } else if (this.debugMode && match) {
          console.log(`[ModMatcher] ✗ Pattern "${pattern.mod.name}" matched regex but failed validation`);
        }
      }
    }

    // If no matches found with pattern key, try all patterns (fallback)
    if (matches.length === 0 && candidates.length < this.patterns.length) {
      if (this.debugMode) {
        console.log(`[ModMatcher] No matches found with pattern key, trying all ${this.patterns.length} patterns...`);
      }
      for (const pattern of this.patterns) {
        if (candidates.includes(pattern)) {
          continue; // Already checked
        }

        const match = normalizedText.match(pattern.regex);
        if (match) {
          let valid = true;
          let captureIndex = 1;
          const capturedValues: number[] = [];

          for (const range of pattern.ranges) {
            if (captureIndex >= match.length) {
              valid = false;
              break;
            }

            const capturedValue = parseFloat(match[captureIndex]);
            if (isNaN(capturedValue)) {
              valid = false;
              break;
            }

            capturedValues.push(capturedValue);

            if (capturedValue < range.min || capturedValue > range.max) {
              valid = false;
              break;
            }

            captureIndex++;
          }

          if (valid) {
            matches.push(pattern.mod.name);
            if (this.debugMode) {
              console.log(`[ModMatcher] ✓ Matched "${pattern.mod.name}" (fallback) with values: [${capturedValues.join(', ')}]`);
            }
          }
        }
      }
    }

    if (this.debugMode) {
      if (matches.length === 0) {
        console.log(`[ModMatcher] ✗ No matches found for: "${normalizedText}"`);
      } else {
        console.log(`[ModMatcher] Found ${matches.length} match(es): [${matches.join(', ')}]`);
      }
    }

    // TODO: When multiple matches occur (e.g., same values in low-tier two-handed vs high-tier one-handed),
    // we need to disambiguate by looking at mod slots. For now, return all matches.
    return matches;
  }

  /**
   * Matches multiple explicit mod texts and returns all matching mod names.
   */
  matchModTexts(explicitModTexts: string[]): string[] {
    const allMatches: string[] = [];
    for (const text of explicitModTexts) {
      const matches = this.matchModText(text);
      allMatches.push(...matches);
    }
    return allMatches;
  }
}

